<template>
    <div class="graph">
        <h2 class="graph__title" v-html="$t('PolarAreaGraph.completed_by_category')"></h2>
        <div class="legend" id="courses-legend"></div>
        <PolarGraph :chartData="chartData" legendId="courses-legend" class="graph__chart" :class="{ graph__empty: isChartDataEmpty}" @on-click-in-graph-element="goToCompletedCourses" />
    </div>
</template>

<script>
    import { mapGetters } from 'vuex'
    import PolarGraph from 'components/user/graphs/PolarGraph'
    import dateHelper, { getYearlyArrayOfDatesInFormat } from 'components/shared/mixins/dateHelper'
    import barHelper from 'helpers/barHelper'

    function getCompletedCategories (completedCourses, selectedYear) {
        let arrayOfDates = getYearlyArrayOfDatesInFormat('Y-MM', selectedYear)

        let completedCategories = completedCourses
            .filter(course => {
                return arrayOfDates.includes(course.certificate?.certificationDate.substring(0, 7))
            })
            .reduce((acc, course) => {
                return acc.concat(course.categories || [])
            }, [])
            .reduce((acc, val) => {
                acc[val.label] = (acc[val.label] || 0) + 1
                return acc
            }, {})

        return Object.keys(completedCategories)
            .filter(key => key !== 'Competence')
            .reduce((obj, key) => {
                return {
                    ...obj,
                    [key]: completedCategories[key]
                }
            }, {})
    }

    function groupCompletedCategories (completedCategories) {
        const sorted = Object.keys(completedCategories)
            .map(key => ({ key, value: completedCategories[key] }))
            .sort(({ value: a }, { value: b }) => b - a)

        const mapDataAndLabels = (items) => {
            return items.reduce((result, { key, value }) => {
                result.labels.push(key)
                result.data.push(value)
                return result
            }, { data: [], labels: [] })
        }

        return {
            main: mapDataAndLabels(sorted.slice(0, 5)),
            rest: mapDataAndLabels(sorted.slice(5))
        }
    }

    export default {
        props: {
            completedCourses: Array
        },
        mixins: [ dateHelper, barHelper ],
        components: {
            PolarGraph
        },
        data () {
            return {
                colors: [
                    'rgba(0, 80, 80, 0.5)',
                    'rgba(112, 205, 201, 0.5)',
                    'rgba(195,170, 172, 0.5)',
                    'rgba(245, 255, 107, 0.5)',
                    'rgba(0, 112, 192, 0.5)',
                    'rgba(197, 154, 210, 0.5)'
                ]
            }
        },
        methods: {
            goToCompletedCourses (data) {
                if (!data) {
                    return
                }

                if (data !== this.$t('PolarAreaGraph.rest')) {
                    let id = this.getIdOfCategoryByLabelInTree(this.categories, data)
                    let option = { label: data, id: id }

                    this.$store.dispatch('CourseFilter/addSelectedOption', option)
                }

                let arrayOfDates = this.getYearlyArrayOfDatesInFormat('Y-MM', this.selectedYear)

                let startDate = new Date(arrayOfDates[0])
                let endDate = new Date(arrayOfDates[arrayOfDates.length - 1])
                endDate.setDate(this.getDaysInMonth(endDate.getMonth() + 1, endDate.getFullYear()))

                this.$router.push({ name: 'user-completed', params: { startDate, endDate } })
            }
        },
        computed: {
            ...mapGetters({
                categories: 'CourseFilter/getCategories',
                getIdOfCategoryByLabelInTree: 'CourseFilter/getIdOfCategoryByLabelInTree',
                graphYear: 'User/getGraphYear'
            }),
            selectedYear () {
                return this.graphYear === 'last-12-months' ? null : this.graphYear
            },
            chartData () {
                let completedCategories = getCompletedCategories(this.completedCourses, this.selectedYear)
                let groupedCategories = groupCompletedCategories(completedCategories)

                let labels = groupedCategories.main.labels
                let data = groupedCategories.main.data

                if (groupedCategories.rest.labels.length > 0) {
                    labels.push(this.$t('PolarAreaGraph.rest'))
                    data.push(groupedCategories.rest.data.reduce((a, b) => a + b))
                }

                let graphData = {
                    labels: labels,
                    datasets: [
                        {
                            backgroundColor: this.colors,
                            data: data
                        }
                    ]
                }

                return graphData
            },
            isChartDataEmpty () {
                return this.chartData.datasets[0].data.length === 0
            }
        },
        created () {
            this.$store.dispatch('CourseFilter/loadCategories')
        }
    }
</script>

<style lang="scss" scoped>
    @include summary_graph;
</style>
